import Card from 'components/card';
import StockCard from 'components/card/StockCard';
import SearchIcon from 'components/icons/SearchIcon';
import ingredientsImg from 'assets/img/ingredients.png';
import mobilityImg from 'assets/img/mobility.png';
import { getStock } from 'interfaces/stock';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdPallet, MdGridView, MdDehaze } from 'react-icons/md';
import MovementTable from './components/MovementTable';
import CreateButton from 'components/buttons/CreateButton';
import { getStockMovement } from 'interfaces/stockMovement';
import { useDisclosure } from '@chakra-ui/hooks';
import StockEntryModal from './components/StockEntryModal';
import StockExitModal from './components/StockExitModal';
import OnBoardingCard from 'views/admin/main/account/coursePage/components/OnBoardingCard';
import { useShepherdTour } from 'react-shepherd';
import SearchSelector from 'components/fields/SearchSelector';
import { getTags } from 'interfaces/tagProduct';

const steps: any = [
    {
        title: '3/5 Créer vos clients',
        text: 'Rendez-vous dans la section Client pour importer vos clients',
        attachTo: {
            element: '.step-customers',
            on: 'left',
        },
        buttons: [
            {
                classes: 'shepherd-button-primary',
                text: 'Importer mes clients',
                action: function (this: any): any {
                    localStorage.setItem('onboarding-step', '3');
                    window.location.href = '/customers';
                },
            },
        ],
        id: 'step-customers',
    },
];

const StockList = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [stock, setStock] = useState(null);
    const [stockMovement, setStockMovement] = useState([]);
    const [toggleState, setToggleState] = useState(1);
    const [onboarding, setOnboarding] = useState(false);
    const [category, setCategory] = useState('all');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [view, setView] = useState<'grid' | 'list'>('list');
    const [tags, setTags] = useState<any[]>([]);
    const [selectedTag, setSelectedTag] = useState('all');
    const [alertFilter, setAlertFilter] = useState<'all' | 'alert' | 'ok' | 'no-threshold'>('all');
    const {
        isOpen: isOpenExit,
        onOpen: onOpenExit,
        onClose: onCloseExit,
    } = useDisclosure();
    const tourOptions = {
        defaultStepOptions: {
            cancelIcon: {
                enabled: true,
            },
        },
        useModalOverlay: true,
    };
    const tour = useShepherdTour({ tourOptions, steps });

    const toggleTab = (index: number) => {
        setToggleState(index);
    };

    const initStock = async () => {
        const res = await getStock();
        if (res.error) {
            navigate('/auth/login');
            return;
        }
        setStock(res.data);
    };

    const initStockMovement = async () => {
        const res = await getStockMovement();
        if (res.error) {
            navigate('/auth/login');
            return;
        }
        setStockMovement(res.data.data);
    };

    const initTags = async () => {
           try {
             const res: any = await getTags();
             if (Array.isArray(res)) setTags(res);
             else if (Array.isArray(res?.data)) setTags(res.data);
             else setTags([]);
           } catch (e) {
             console.error('Erreur lors du chargement des tags:', e);
             setTags([]);
           }
         };

         const resolveProductTag = (p: any) => {
               const raw = p?.productTag ?? p?.tagId ?? null;
               if (!raw) return null;
               if (typeof raw === 'object') return raw;
               return tags.find((t: any) => t._id === raw) || null;
         };

    useEffect(() => {
        const onboardingStep = localStorage.getItem('onboarding-step');
        const queryParameters = new URLSearchParams(window.location.search);
        const page = queryParameters.get('page');
        const searchParam = queryParameters.get('search');
        const tagParam = queryParameters.get('tag');
        const viewParam = queryParameters.get('view');
        if (searchParam) setSearch(searchParam);
        if (tagParam) setSelectedTag(tagParam);
        if (viewParam === 'list' || viewParam === 'grid'){
            setView(viewParam as 'list' | 'grid');
        } else{
            const savedView = localStorage.getItem('stock-view');
            if (savedView === 'list' || savedView === 'grid') setView(savedView as 'list' | 'grid');
        }
        if (page && page === 'stock') {
            setToggleState(5);
            onOpen();
        }
        initStock();
        initStockMovement();
        initTags();
        if (onboardingStep === '2') setOnboarding(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
           const sp = new URLSearchParams(window.location.search);
           if (search && search.trim() !== '') sp.set('search', search);
           else sp.delete('search');
           if (selectedTag && selectedTag !== 'all') sp.set('tag', selectedTag);
           else sp.delete('tag');
           sp.set('view', view);
           const newUrl = `${window.location.pathname}${sp.toString() ? `?${sp.toString()}` : ''}`;
           window.history.replaceState(null, '', newUrl);
         }, [search, selectedTag]);

    useEffect(() => {
        localStorage.setItem('stock_view', view);
    }, [view]);

    const SkelettonProduct = () => {
        return (
            <div
                className={`animate-pulse relative flex items-center rounded-[20px] bg-clip-border border px-[18px] py-4 shadow-3xl shadow-shadow-500 hover:cursor-pointer dark:!bg-navy-800 dark:shadow-none`}
            >
                <div className="grid grid-cols-12">
                    <div className="-bottom-12 col-span-4 flex h-[87px] w-[87px] items-center justify-center rounded-full">
                        <div className="h-[80px] w-[80px] rounded-xl bg-gray-200"></div>
                    </div>
                    <div className="col-span-8 ml-4">
                        <div className="mt-2 w-[99%] h-4 bg-navy-700/50 rounded-full"></div>
                        <div className="my-1 w-[60px] h-2 bg-gray-600 rounded-full"></div>
                        <p className=" mb-1 text-sm font-medium text-gray-600">
                            <div className="w-5 h-3 bg-gray-600 rounded-full"></div>
                        </p>
                        <div className="w-[60px] h-5 bg-gray-600  rounded-full">
                            {' '}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const StockMovement = () => {
        return (
            <div className="col-span-12">
                {stockMovement && stockMovement.length > 0 && (
                    <MovementTable tableData={stockMovement} />
                )}
                {stockMovement && stockMovement.length === 0 && (
                    <div className="col-span-12 mt-10">
                        <img
                            className="mx-auto flex h-[120px] w-[120px] items-center justify-center"
                            src={mobilityImg}
                            alt=""
                        />
                        <div className="font-large mt-5 flex items-center justify-center gap-5 rounded-xl">
                            <button
                                className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                onClick={onOpen}
                            >
                                Entrer du stock
                            </button>
                            <button
                                className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-11"
                                onClick={onOpenExit}
                            >
                                Sortir du stock
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const searchProduct = (product: any, search: string) => {
        if (!search) return true;

        const escapeRegex = (text: string) =>
            text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const fuzzify = (term: string): RegExp => {
            const escaped = escapeRegex(term);
            const length = term.length;

            if (length <= 1) {
                return new RegExp(`\\b${escaped}\\b`, 'i');
            }

            if (length <= 4) {
                const loose = escaped.split('').join('.{0,2}');
                return new RegExp(loose, 'i');
            }

            const fuzzy = escaped.split('').join('.*');
            return new RegExp(fuzzy, 'i');
        };

        const terms = search.trim().toLowerCase().split(/\s+/);

        return terms.every((term) => {
            const regex = fuzzify(term);
            for (const key in product) {
                const value = product[key];
                if (
                    typeof value === 'string' &&
                    regex.test(value.toLowerCase())
                ) {
                    return true;
                }
            }
            return false;
        });
    };

    const filterStockPerZone: any = (stock: any, areaIndex: number) => {
        const areas = ['', '', 'zone-a', 'zone-b', 'zone-c', ''];
        if (areas[areaIndex] === '') return stock;
        let newStock = JSON.parse(JSON.stringify(stock));
        newStock = newStock.map((item: any) => {
            let quantity = 0;
            item.inventory.forEach((inventory: any) => {
                if (inventory.area === areas[areaIndex]) {
                    quantity += inventory.quantity;
                }
            });
            item.quantity = quantity;
            return item;
        });
        if (areaIndex > 1)
            return newStock.filter((item: any) => item.quantity);
        return newStock;
    };

    const refreshData = async () => {
        initStockMovement();
        initStock();
    };

    return (
        <>
            <StockEntryModal
                onClose={onClose}
                isOpen={isOpen}
                stock={stock}
                onDone={refreshData}
            />
            <StockExitModal
                onClose={onCloseExit}
                isOpen={isOpenExit}
                stock={stock}
                onDone={refreshData}
            />
            {onboarding && (
                <div className="mt-5">
                    <OnBoardingCard
                        title="Créer vos produits"
                        actualStep={2}
                        totalStep={5}
                        description="Vous pouvez créer tout vos produits ici avant de passer à l'étape suivante"
                        onClick={() => tour.start()}
                    />
                </div>
            )}
            <Card extra={'w-full h-full sm:overflow-auto p-5 border'}>
                
                {/* Tabs */}
                <div className="h-full w-full flex-col px-4 lg:px-3">
                
                    <div className="mt-2 flex w-full justify-between gap-0 sm:gap-0 md:gap-2 xl:gap-14 overflow-hidden border-b border-gray-200 text-3xl lg:gap-[50px] 2xl:justify-center 2xl:gap-[70px]">
                    {toggleState < 5 && (
                        <div className="hidden flex lg:block">
                            <div className="flex gap-2 ml-auto">
                                 <MdGridView
                                    size={20}
                                    className={`${view === 'grid' && 'text-toola-500'} cursor-pointer`}
                                    title="Vue cartes"
                                    onClick={() => setView('grid')}
                                />
                                <MdDehaze
                                    size={20}
                                    className={`${view === 'list' && 'text-toola-500'} cursor-pointer`}
                                    title="Vue tableau"
                                    onClick={() => setView('list')}
                                />
                            </div>
                        </div>
                    )}
                        <div
                            className={
                                toggleState === 1
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(1)}
                        >
                            <p
                                className={`text-sm md:text-lg text-[#0260CB] dark:text-white`}
                            >
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Toutes les zones
                            </p>
                        </div>

                        <div
                            className={
                                toggleState === 2
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(2)}
                        >
                            <p className="text-sm md:text-lg text-navy-700 dark:text-white">
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Zone A
                            </p>
                        </div>

                        <div
                            className={
                                toggleState === 3
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(3)}
                        >
                            <p className="text-sm md:text-lg text-navy-700 dark:text-white">
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Zone B
                            </p>
                        </div>

                        <div
                            className={
                                toggleState === 4
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(4)}
                        >
                            <p className="text-sm md:text-lg text-navy-700 dark:text-white">
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Zone C
                            </p>
                        </div>

                        {/* <div
                                className={
                                    toggleState === 5
                                        ? " flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400"
                                        : "flex items-center gap-2 pb-[20px] hover:cursor-pointer"
                                }
                                onClick={() => toggleTab(5)}
                            >
                                <p className="text-lg text-navy-700 dark:text-white">
                                    <MdOutlineMoveDown />
                                </p>
                                <p className="text-sm md:text-lg md:text-[18px] font-medium text-navy-700 dark:text-white">
                                    Mouvements
                                </p>
                            </div> */}
                    </div>
                </div>
                {/* Search bar */}
                <div className="grid max-w-full grid-cols-12 items-center gap-3 rounded-xl pt-5">
                    <div className="col-span-12 flex h-[38px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white lg:col-span-4">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Recherche...."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                        />
                    </div>
                    {/* onClick={() => navigate("/create-product")} */}
                    {stock && stock.length > 0 && toggleState !== 5 && (
                        <>
                            <CreateButton
                                value="Créer un produit"
                                extra=" text-base col-start-1 col-span-6 md:col-span-4 lg:col-start-8 lg:col-span-3 3xl:col-start-9 3xl:col-span-2"
                                onClick={() => navigate('/create-product')}
                            />
                            <button
                                className="col-span-6 rounded-xl border-2 border-[#0260CB] py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 md:col-span-3 lg:col-span-2"
                                onClick={() => navigate('/stock/movements')}
                            >
                                Gérer le stock
                            </button>
                        </>
                    )}
                    {toggleState === 5 &&
                        stock &&
                        stock.length > 0 &&
                        stockMovement.length > 0 && (
                            <>
                                <button
                                    className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                    onClick={onOpen}
                                >
                                    Entrer du stock
                                </button>
                                <button
                                    className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-11"
                                    onClick={onOpenExit}
                                >
                                    Sortir du stock
                                </button>
                            </>
                        )}
                </div>
                <div className="flex justify-between">
                    <div className="mt-4 flex gap-2 overflow-x-auto h-7">
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'all' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('all')}
                        >
                            Tout
                        </p>
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'brut' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('brut')}
                        >
                            Produit brut
                        </p>
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'transformed' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('transformed')}
                        >
                            Produit transformé
                        </p>
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'assembled' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('assembled')}
                        >
                            Produit assemblé
                        </p>
                    </div>
                    <div className="pt-3 z-50 w-[180px] text-sm">
                    <SearchSelector
                        displayKey="name"
                        options={[{ name: 'Tous les tags', _id: 'all' }, ...tags]}
                        value={selectedTag}
                        onSelect={(t: any) => setSelectedTag(t?._id ?? 'all')}
                        defaultKey="_id"
                        canCreate={false}
                        inputSmall={true}
                    />
                    </div>
                </div>
                {/* Skelleton Loader */}
                {!stock && (
                    <div className="z-0 col-span-12 mb-10 mt-2 grid h-full w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                        {[
                            1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
                            16,
                        ].map((item, index) => (
                            <SkelettonProduct key={index} />
                        ))}
                    </div>
                )}
                {/* Items list */}
                <div className="z-0 col-span-12 mb-10 mt-2 grid h-full w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                                        
                    {/* Items list */}
                    {stock && toggleState < 5 && (() => {
                      const filteredStock = filterStockPerZone(stock, toggleState)
                        .filter((e: any) => searchProduct(e, search))
                        .filter((e: any) => (category === 'all' ? true : e.productType === category))
                        .filter((e: any) => selectedTag === 'all' ? true : (resolveProductTag(e)?._id === selectedTag))
                        .filter((e: any) => {
                            if (alertFilter === 'all') return true;
                            const quantity = Number(e.quantity ?? 0);
                            const threshold = Number(e.alert);
                            const hasThreshold = !Number.isNaN(threshold);
                            if (!hasThreshold) return alertFilter === 'no-threshold';
                            if (alertFilter === 'alert') return quantity <= threshold;
                            if (alertFilter === 'ok') return quantity > threshold;
                            return true;
                        }
                        )
                        .reverse();
                    
                      return (
                        <>
                          {/* Vue tableau */}
                          {view === 'list' && (
                            <div className="z-0 col-span-12 mb-10 mt-4 rounded-lg border">
                              <div className="relative overflow-x-auto">
                                <table className="w-full text-sm text-left text-gray-500">
                                  <thead className="text-gray-700 bg-gray-50">
                                    <tr>
                                      <th scope="col" className="px-2 py-3">Produit</th>
                                      <th scope="col" className="px-2 py-3">Référence</th>
                                      <th scope="col" className="px-2 py-3">Type</th>
                                      <th scope="col" className="px-2 py-3">Unité</th>
                                      <th scope="col" className="px-2 py-3">Stock</th>
                                      <th scope="col" className="px-2 py-3 gap-4 justify-between">Alerte
                                      <select
                                        value={alertFilter}
                                        onChange={(e) => setAlertFilter(e.target.value as any)}
                                        className="ml-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
                                      >
                                        <option value="all">Toutes</option>
                                        <option value="alert">En alerte</option>
                                        <option value="ok">En stock</option>
                                     </select>
                                      </th>
                                      <th scope="col" className="px-2 py-3">Tags</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredStock.map((item: any, index: number) => (
                                      <tr
                                        key={item._id || index}
                                        className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                        onClick={() => navigate(`/update-product/${item._id}`)}
                                      >
                                        <td className="px-2 py-3 max-w-[260px]">
                                          <p className="font-medium text-gray-900 truncate">{item.name}</p>
                                        </td>
                                        <td className="px-2 py-3">{item.ref || '-'}</td>
                                        <td className="px-2 py-3">
                                          {item.productType === 'brut'
                                            ? 'Produit brut'
                                            : item.productType === 'transformed'
                                            ? 'Produit transformé'
                                            : item.productType === 'assembled'
                                            ? 'Produit assemblé'
                                            : '-'}
                                        </td>
                                        <td className="px-2 py-3">{item.unity || '-'}</td>
                                        <td className="px-2 py-3">{item.quantity ?? 0}</td>
                                        <td className="px-2 py-3">
                                            <span className="text-gray-700">Seuil: </span>
                                        {(() => {
                                            const quantity = Number(item.quantity ?? 0);
                                            const alert = Number(item.alert ?? NaN);
                                            return (
                                        <>
                                            {quantity <= alert && (
                                        <>
                                            <span className="text-red-500">alerte </span>
                                            <span className="text-red-500">!</span>
                                        </>
                                            )}
                                            {quantity > alert && <span className="text-green-500">OK</span>}
                                        </>
                                            );
                                        })()}
                                        </td>
                                        <td className="px-2 py-3">
                                        {(() => {
                                            const tag = resolveProductTag(item);
                                            return tag ? (
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="inline-block w-4 h-4 rounded-full border border-white shadow-sm"
                                                style={{ backgroundColor: tag.color }}
                                                title={tag.name}
                                            />
                                            <span className="text-xs text-gray-700">{tag.name}</span>
                                        </div>
                                            ) : (
                                            '-'
                                            );
                                        })()}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {filteredStock.length === 0 && (
                                <div className="p-6 text-center text-gray-500">Aucun produit</div>
                              )}
                            </div>
                          )}
                    
                            {/* View cards */}
                          {view === 'grid' && (
                            <div className="z-0 col-span-12 mb-10 mt-2 grid h-full w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                              {filteredStock.map((item: any, index: number) => (
                                <StockCard
                                  id={item._id}
                                  key={index}
                                  name={item.name}
                                  type={item.type}
                                  alert={item.alert}
                                  imgs={item.imageUrls}
                                  img={item.imageUrl}
                                  productType={item.productType}
                                  unity={item.unity}
                                  quantity={item.quantity}
                                  reference={item.ref}
                                  color={item.colorCode}
                                />

                              ))}
                              {filteredStock.length === 0 && (
                                <div className="col-span-12 p-6 text-center text-gray-500">Aucun produit</div>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* nothing product */}
                    {stock &&
                      toggleState < 5 &&
                      filterStockPerZone(stock, toggleState).length === 0 &&
                      stock.length !== 0 && (
                        <div className="col-span-12">
                          <img
                            className="mx-auto flex h-[120px] w-[120px] items-center justify-center"
                            src={mobilityImg}
                            alt=""
                          />
                          <p className="mt-3 text-center">
                            Déplacez du stock de zone en zone en faisant une entrée-sortie.
                          </p>
                          <p className="mt-3 text-center">Vous pourrez bientôt le faire ici en 2 cliques !</p>
                          <div className="font-large flex items-center justify-center rounded-xl bg-white py-2 text-base text-gray-600 dark:!bg-navy-900 dark:text-white">
                            <button
                              className="linear mt-5 flex items-center justify-center rounded-xl bg-toola-500 px-10 py-2 text-base font-medium text-white hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                              onClick={() => setToggleState(5)}
                            >
                              Mouvement de stock
                            </button>
                          </div>
                        </div>
                    )}
                    
                    {stock && stock.length === 0 && toggleState !== 5 && (
                      <div className="col-span-12">
                        <img
                          className="mx-auto flex h-[120px] w-[120px] items-center justify-center"
                          src={ingredientsImg}
                          alt=""
                        />
                        <div className="font-large flex items-center justify-center rounded-xl bg-white py-2 text-base text-gray-600 dark:!bg-navy-900 dark:text-white">
                          <button
                            className="linear mt-5 flex items-center justify-center rounded-xl bg-[#0260CB] px-10 py-2 text-base font-medium text-white hover:bg-[#01499b] active:bg-[#024086] dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                            onClick={() => navigate('/create-product')}
                          >
                            Créer votre premier produit
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {toggleState === 5 && <StockMovement />}
                </div>
            </Card>
        </>
    );
};

export default StockList;
